import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import Slider from "react-slick";
import { IoCloseOutline, IoArrowBack, IoArrowForward, IoCalendarClear } from "react-icons/io5";
import { MdCalendarMonth, MdChevronLeft, MdChevronRight, MdGroups, MdDiamond } from "react-icons/md";
import { FaLocationDot } from "react-icons/fa6";
import PhoneInputField from "@/Common/PhoneInputField";
import { useAlert } from "@/Common/CustomAlert/AlertContext";
import SmartSearchDrawer from "./SmartSearchDrawer";
import SmartSearchPackageCard from "./SmartSearchPackageCard";
import SmartSearchStepDestination from "./SmartSearchStepDestination";
import { fetchRandomReviewList } from "@/services/api/common";
import {
  fetchHolidayExperienceTypes,
  fetchHolidayTravelTypes,
  fetchRandomHolidayDestinations,
  fetchSmartHolidaysList,
  searchHoliday,
  submitSmartSearchEnquiry,
} from "@/services/api/holidays";
import useIsDesktop from "@/hooks/useIsDesktop";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import useSlickArrows from "@/hooks/useSlickArrows";
import { parsePhoneForApi, validatePhoneValue } from "@/utils/phoneInput";
import { isIndianUser } from "@/utils/getCountryCode";
import { normalizeHolidaySearchData } from "@/utils/holidaySearch";
import { withRegionPath } from "@/utils/session";
import {
  buildSmartSearchEnquiryPayload,
  buildSmartSearchPayload,
  DURATION_OPTIONS,
  isSmartSearchSuccess,
  normalizeSmartSearchDestinations,
  normalizeSmartSearchList,
  normalizeSmartSearchOptions,
  normalizeSmartSearchReviews,
  sanitizeSmartSearchImageUrl,
  SMART_SEARCH_STEPS,
  TRAVEL_MONTHS,
} from "@/utils/smartSearch";
import styles from "./SmartSearchModal.module.css";
import { PiArrowBendUpLeftBold } from "react-icons/pi";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EXPERIENCE_SLIDES_TO_SHOW = 4;
const MIN_CUSTOM_DURATION_DAYS = 1;
const MAX_CUSTOM_DURATION_DAYS = 25;

function mapSearchItemToDestination(item) {
  return {
    id: item.cityId || item.countryId || item.stateId || item.regionId || item.id,
    label: item.label || item.title || "",
    image: sanitizeSmartSearchImageUrl(
      item.raw?.imageURL ||
      item.raw?.imageUrl ||
      item.raw?.imagePath ||
      item.raw?.webImagePath ||
      ""
    ),
    continentId: Number(item.raw?.continentId) || 0,
    regionId: Number(item.regionId) || 0,
    countryId: Number(item.countryId) || 0,
    stateId: Number(item.stateId) || 0,
    cityId: Number(item.cityId) || 0,
  };
}

function LoadingBlock({ className = "" }) {
  return (
    <div className={`${styles.loadingWrap} ${className}`.trim()}>
      <span className={styles.spinner} role="status" aria-label="Loading" />
    </div>
  );
}

function Stepper({ currentStep }) {
  const numericStep = typeof currentStep === "number" ? currentStep : 5;

  return (
    <div className={styles.stepperBar}>
      <div className={styles.stepper}>
        {SMART_SEARCH_STEPS.map((step, index) => {
          const done =
            numericStep > step.id || currentStep === "results" || currentStep === "enquiry";
          const active = numericStep === step.id;
          const lineDone =
            numericStep > step.id ||
            currentStep === "results" ||
            currentStep === "enquiry";

          return (
            <Fragment key={step.id}>
              <div
                className={`${styles.stepColumn} ${active ? styles.stepItemActive : done ? styles.stepItemDone : ""
                  }`.trim()}
              >
                <span className={styles.stepCircle}>{step.id}</span>
                <span className={styles.stepLabel}>{step.label}</span>
              </div>
              {index < SMART_SEARCH_STEPS.length - 1 ? (
                <span
                  className={`${styles.stepLine} ${lineDone ? styles.stepLineDone : ""}`}
                  aria-hidden
                />
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

function SelectionChips({ selection }) {
  if (!selection.destination && !selection.duration && !selection.travelType) {
    return null;
  }

  return (
    <div className={styles.chips}>
      {selection.destination ? (
        <span className={styles.chip}>
          <FaLocationDot size={10} />
          {selection.destination.label}
        </span>
      ) : null}
      {selection.duration ? (
        <span className={styles.chip}>
          <IoCalendarClear size={12} />
          {selection.duration.label}
        </span>
      ) : null}
      {selection.travelType ? (
        <span className={styles.chip}>
          <MdGroups size={14} />
          {selection.travelType.label}
        </span>
      ) : null}
      {selection.experienceType ? (
        <span className={styles.chip}>
          <MdDiamond size={12} />
          {selection.experienceType.label}
        </span>
      ) : null}
    </div>
  );
}

export default function SmartSearchModal({ open, onClose }) {
  useBodyScrollLock(open);
  const { showAlert } = useAlert();
  const { isDesktop, mounted } = useIsDesktop(900);
  const isMobileWizard = mounted && !isDesktop;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [destinationsLoading, setDestinationsLoading] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [travelTypes, setTravelTypes] = useState([]);
  const [experienceTypes, setExperienceTypes] = useState([]);
  const [results, setResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [drawerPackage, setDrawerPackage] = useState(null);
  const [selection, setSelection] = useState({
    destination: null,
    duration: null,
    travelType: null,
    experienceType: null,
  });
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    destination: "",
    travelMonth: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [customDurationDays, setCustomDurationDays] = useState("");
  const [customDurationFocused, setCustomDurationFocused] = useState(false);
  const [customDurationExpanded, setCustomDurationExpanded] = useState(false);
  const customDurationInputRef = useRef(null);
  const experienceSliderRef = useRef(null);

  const {
    canPrev: canExperiencePrev,
    canNext: canExperienceNext,
    slickArrowEvents: experienceSlickArrowEvents,
  } = useSlickArrows(
    experienceTypes.length,
    EXPERIENCE_SLIDES_TO_SHOW,
    experienceTypes.length
  );

  const fewExperienceSlides = experienceTypes.length <= EXPERIENCE_SLIDES_TO_SHOW;

  const experienceSlickSettings = useMemo(
    () => ({
      dots: false,
      infinite: false,
      centerMode: false,
      speed: 400,
      slidesToShow: EXPERIENCE_SLIDES_TO_SHOW,
      slidesToScroll: 1,
      arrows: false,
      swipe: true,
      draggable: true,
      touchMove: true,
      ...experienceSlickArrowEvents,
      responsive: [
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 1,
          },
        },
      ],
    }),
    [experienceSlickArrowEvents]
  );

  const resetState = useCallback(() => {
    setStep(1);
    setCustomDurationDays("");
    setCustomDurationFocused(false);
    setCustomDurationExpanded(false);
    setSelection({
      destination: null,
      duration: null,
      travelType: null,
      experienceType: null,
    });
    setResults([]);
    setSearchQuery("");
    setSearchResults([]);
    setSearchLoading(false);
    setDrawerPackage(null);
    setForm({
      fullName: "",
      email: "",
      phone: "",
      destination: "",
      travelMonth: "",
    });
    setFormErrors({});
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    resetState();
    setDestinationsLoading(true);
    setReviewsLoading(true);

    fetchRandomHolidayDestinations(10)
      .then((destRes) => {
        setDestinations(normalizeSmartSearchDestinations(destRes));
      })
      .catch(() => setDestinations([]))
      .finally(() => setDestinationsLoading(false));

    fetchRandomReviewList({ moduleType: "Holiday", noOfreview: 5 })
      .then((reviewRes) => {
        setReviews(normalizeSmartSearchReviews(reviewRes));
      })
      .catch(() => setReviews([]))
      .finally(() => setReviewsLoading(false));

    fetchHolidayTravelTypes()
      .then((travelRes) => setTravelTypes(normalizeSmartSearchOptions(travelRes)))
      .catch(() => setTravelTypes([]));

    fetchHolidayExperienceTypes()
      .then((experienceRes) =>
        setExperienceTypes(normalizeSmartSearchOptions(experienceRes))
      )
      .catch(() => setExperienceTypes([]));

    return undefined;
  }, [open, resetState]);

  useEffect(() => {
    if (!open || !reviews.length) return undefined;
    const timer = window.setInterval(() => {
      setReviewIndex((prev) => (prev + 1) % reviews.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [open, reviews.length]);

  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    setSearchLoading(true);
    const timer = window.setTimeout(() => {
      searchHoliday(searchQuery.trim())
        .then((response) => {
          setSearchResults(
            normalizeHolidaySearchData(response).map(mapSearchItemToDestination)
          );
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const activeReview = reviews[reviewIndex] || reviews[0];

  const summary = useMemo(
    () => ({
      destination: selection.destination?.label || "",
      duration: selection.duration?.label || "",
      travelType: selection.travelType?.label || "",
      experience: selection.experienceType?.label || "",
    }),
    [selection]
  );

  const canContinue = useMemo(() => {
    if (step === 1) return Boolean(selection.destination);
    if (step === 2) {
      const duration = selection.duration;
      if (!duration) return false;
      if (duration.id === "custom") {
        return (duration.nights?.length ?? 0) > 0;
      }
      return true;
    }
    if (step === 3) return Boolean(selection.travelType);
    if (step === 4) return Boolean(selection.experienceType);
    return true;
  }, [step, selection]);

  const completeSmartSearch = useCallback(
    async (selectionSnapshot) => {
      setLoading(true);
      try {
        const response = await fetchSmartHolidaysList(
          buildSmartSearchPayload(selectionSnapshot)
        );
        const list = normalizeSmartSearchList(response);
        if (list.length) {
          setResults(list);
          setStep("results");
        } else {
          setForm((prev) => ({
            ...prev,
            destination: selectionSnapshot.destination?.label || prev.destination,
          }));
          setStep("enquiry");
        }
      } catch (error) {
        showAlert({
          title: "Unable to fetch packages",
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Please try again.",
        });
      } finally {
        setLoading(false);
      }
    },
    [showAlert]
  );

  const handleContinue = async () => {
    if (step === 4) {
      await completeSmartSearch(selection);
      return;
    }

    if (typeof step === "number" && step < 4) {
      setStep(step + 1);
    }
  };

  const handleDestinationSelect = useCallback(
    (item, options = {}) => {
      setSelection((prev) => ({ ...prev, destination: item }));
      if (isMobileWizard || options.fromSearch) setStep(2);
    },
    [isMobileWizard]
  );

  const buildCustomDurationOption = useCallback((rawValue) => {
    const days = parseInt(String(rawValue).trim(), 10);
    if (!Number.isFinite(days) || days < MIN_CUSTOM_DURATION_DAYS) return null;
    const clamped = Math.min(
      Math.max(days, MIN_CUSTOM_DURATION_DAYS),
      MAX_CUSTOM_DURATION_DAYS
    );
    return {
      id: "custom",
      label: `${clamped} Day${clamped === 1 ? "" : "s"}`,
      nights: [clamped],
    };
  }, []);

  const handleDurationSelect = useCallback(
    (option) => {
      setCustomDurationDays("");
      setCustomDurationExpanded(false);
      setSelection((prev) => ({ ...prev, duration: option }));
      if (isMobileWizard) setStep(3);
    },
    [isMobileWizard]
  );

  const handleCustomDurationChange = useCallback(
    (event) => {
      let value = event.target.value.replace(/\D/g, "");
      if (value) {
        const num = parseInt(value, 10);
        if (num > MAX_CUSTOM_DURATION_DAYS) value = String(MAX_CUSTOM_DURATION_DAYS);
        else if (num < MIN_CUSTOM_DURATION_DAYS) value = String(MIN_CUSTOM_DURATION_DAYS);
      }
      setCustomDurationDays(value);
      const customOption = buildCustomDurationOption(value);
      setSelection((prev) => ({ ...prev, duration: customOption }));
    },
    [buildCustomDurationOption]
  );

  useEffect(() => {
    if (step === 2 && isMobileWizard && selection.duration?.id === "custom") {
      setCustomDurationExpanded(true);
    }
  }, [step, isMobileWizard, selection.duration?.id]);

  const expandCustomDuration = useCallback(() => {
    setCustomDurationExpanded(true);
    setSelection((prev) => ({
      ...prev,
      duration: prev.duration?.id === "custom"
        ? prev.duration
        : { id: "custom", label: "Custom Duration", nights: [] },
    }));
    window.setTimeout(() => customDurationInputRef.current?.focus(), 0);
  }, []);

  const commitCustomDurationAndAdvance = useCallback(() => {
    const customOption = buildCustomDurationOption(customDurationDays);
    if (!customOption) return false;
    setSelection((prev) => ({ ...prev, duration: customOption }));
    if (isMobileWizard) setStep(3);
    return true;
  }, [buildCustomDurationOption, customDurationDays, isMobileWizard]);

  const handleCustomDurationKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      commitCustomDurationAndAdvance();
    },
    [commitCustomDurationAndAdvance]
  );

  const handleTravelTypeSelect = useCallback(
    (item) => {
      setSelection((prev) => ({ ...prev, travelType: item }));
      if (isMobileWizard) setStep(4);
    },
    [isMobileWizard]
  );

  const handleExperienceSelect = useCallback(
    (item) => {
      setSelection((prev) => ({ ...prev, experienceType: item }));
      if (isMobileWizard && !loading) {
        void completeSmartSearch({ ...selection, experienceType: item });
      }
    },
    [completeSmartSearch, isMobileWizard, loading, selection]
  );

  /** Clears only the step you are leaving (earlier steps stay selected). */
  const clearLeavingStepSelection = useCallback((leavingStep) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (leavingStep === 2) next.duration = null;
      else if (leavingStep === 3) next.travelType = null;
      else if (leavingStep === 4 || leavingStep === "results") {
        next.experienceType = null;
      }
      return next;
    });

    if (leavingStep === 2) {
      setCustomDurationDays("");
      setCustomDurationFocused(false);
      setCustomDurationExpanded(false);
    }
  }, []);

  const handleBack = () => {
    if (step === "enquiry") {
      setStep(4);
      return;
    }
    if (step === "results") {
      // setResults([]);
      // clearLeavingStepSelection("results");
      setStep(4);
      return;
    }
    if (typeof step === "number" && step > 1) {
      clearLeavingStepSelection(step);
      setStep(step - 1);
    }
  };

  const validateEnquiry = () => {
    const nextErrors = {};
    if (!form.fullName.trim()) nextErrors.fullName = "Full name is required";
    if (!form.email.trim()) nextErrors.email = "Email is required";
    else if (!EMAIL_RE.test(form.email.trim())) nextErrors.email = "Enter a valid email";

    const phoneError = validatePhoneValue(form.phone);
    if (phoneError) nextErrors.phone = phoneError;

    if (!form.destination.trim()) nextErrors.destination = "Destination is required";
    if (!form.travelMonth) nextErrors.travelMonth = "Select travel month";

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmitEnquiry = async () => {
    if (!validateEnquiry()) return;

    const parsedPhone = parsePhoneForApi(form.phone);
    if (!parsedPhone?.phoneNo) {
      setFormErrors((prev) => ({ ...prev, phone: "Enter a valid mobile number" }));
      return;
    }

    setLoading(true);
    try {
      const response = await submitSmartSearchEnquiry(
        buildSmartSearchEnquiryPayload(
          selection,
          {
            ...form,
            countryCode: parsedPhone.countryCode,
            mobile: parsedPhone.phoneNo,
          },
          typeof window !== "undefined" ? window.location.href : ""
        )
      );

      if (!isSmartSearchSuccess(response)) {
        throw new Error(response?.message || "Unable to submit enquiry.");
      }

      onClose?.();
      window.location.href = withRegionPath("/thank-you?context=enquiry");
    } catch (error) {
      showAlert({
        title: "Submission failed",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to submit enquiry. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTopBack = () => {
    if (step === 1) {
      onClose?.();
      return;
    }
    handleBack();
  };

  const showWizardFooter = !isMobileWizard || step === "results" || step === "enquiry";
  const showFooterBack = !isMobileWizard;

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <>
      <div className={styles.root}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.mobileBackBtn}
            aria-label={step === 1 ? "Close smart search" : "Go back"}
            onClick={handleTopBack}
          >
            <IoArrowBack size={22} />
          </button>
          <Image
            src="/images/logo.png"
            alt="Next Holidays"
            width={160}
            height={42}
            className={styles.logo}
            quality={70}
            loading="lazy"
          />
          <button type="button" className={styles.closeBtn} aria-label="Close smart search" onClick={onClose}>
            <IoCloseOutline size={24} />
          </button>
        </div>

        <div className={styles.shell}>
          <div className={styles.panel}>
            <Stepper currentStep={step} />

            {step !== 1 && step !== "enquiry" ? <SelectionChips selection={selection} /> : null}

            {step === 1 ? (
              <SmartSearchStepDestination
                selection={selection}
                onSelectDestination={handleDestinationSelect}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                showSuggestions={showSuggestions}
                setShowSuggestions={setShowSuggestions}
                searchResults={searchResults}
                searchLoading={searchLoading}
                destinations={destinations}
                destinationsLoading={destinationsLoading}
                reviewsLoading={reviewsLoading}
                activeReview={activeReview}
              />
            ) : null}

            {step === 2 ? (
              <>
                <h1 className={styles.title}>
                  How Many Days <span className={styles.titleAccent}>Are You Planning?</span>
                </h1>
                <div className={`${styles.optionGrid} ${styles.durationGrid}`}>
                  {DURATION_OPTIONS.filter((option) => option.id !== "custom").map(
                    (option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`${styles.optionBtn} ${selection.duration?.id === option.id ? styles.optionBtnActive : ""
                          }`.trim()}
                        onClick={() => handleDurationSelect(option)}
                      >
                        {option.label}
                      </button>
                    )
                  )}
                  {isMobileWizard && customDurationExpanded ? (
                    <div className={styles.customDurationPill}>
                      <input
                        ref={customDurationInputRef}
                        id="smart-search-custom-days"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min={MIN_CUSTOM_DURATION_DAYS}
                        max={MAX_CUSTOM_DURATION_DAYS}
                        autoComplete="off"
                        placeholder=""
                        value={customDurationDays}
                        onChange={handleCustomDurationChange}
                        onKeyDown={handleCustomDurationKeyDown}
                        className={styles.customDurationPillInput}
                        aria-label="Custom duration in days"
                      />
                      <span className={styles.customDurationUnit}>Days</span>
                      <button
                        type="button"
                        className={styles.customDurationGo}
                        disabled={!buildCustomDurationOption(customDurationDays)}
                        onClick={commitCustomDurationAndAdvance}
                        aria-label="Continue"
                      >
                        <IoArrowForward size={18} />
                      </button>
                    </div>
                  ) : isMobileWizard ? (
                    <button
                      type="button"
                      className={`${styles.optionBtn} ${
                        selection.duration?.id === "custom" ? styles.optionBtnActive : ""
                      }`.trim()}
                      onClick={expandCustomDuration}
                    >
                      Custom Duration
                    </button>
                  ) : (
                    <div
                      className={`${styles.optionBtn} ${styles.customDurationBtn} ${
                        selection.duration?.id === "custom" || customDurationFocused
                          ? styles.optionBtnActive
                          : ""
                      }`.trim()}
                      onClick={() => customDurationInputRef.current?.focus()}
                      role="presentation"
                    >
                      <input
                        ref={customDurationInputRef}
                        id="smart-search-custom-days"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min={MIN_CUSTOM_DURATION_DAYS}
                        max={MAX_CUSTOM_DURATION_DAYS}
                        autoComplete="off"
                        placeholder="Custom Duration"
                        value={customDurationDays}
                        onChange={handleCustomDurationChange}
                        onKeyDown={handleCustomDurationKeyDown}
                        onFocus={() => setCustomDurationFocused(true)}
                        onBlur={() => setCustomDurationFocused(false)}
                        className={styles.customDurationInput}
                        aria-label="Custom duration in days"
                      />
                    </div>
                  )}
                </div>
              </>
            ) : null}

            {step === 3 ? (
              <>
                <h1 className={styles.title}>
                  Who Are You <span className={styles.titleAccent}>Travelling With?</span>
                </h1>
                <div className={styles.typeGrid}>
                  {travelTypes.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`${styles.typeCard} ${selection.travelType?.id === item.id ? styles.typeCardActive : ""
                        }`.trim()}
                      onClick={() => handleTravelTypeSelect(item)}
                    >
                      {item.image ? (
                        <img src={item.image} alt={item.label} className={styles.typeImage} />
                      ) : (
                        <div className={styles.typeImage} />
                      )}
                      <p className={styles.typeLabel}>{item.label}</p>
                    </button>
                  ))}
                </div>
              </>
            ) : null}

            {step === 4 ? (
              <>
                <h1 className={styles.title}>
                  What Kind Of Experience{" "}
                  <span className={styles.titleAccent}>Do You Want?</span>
                </h1>
                {mounted && isDesktop ? (
                  <div
                    className={`${styles.carouselWrap} ${styles.carouselWrapDesktop} ${styles.experienceCarouselWrap}`}
                  >
                    <div
                      className={`${styles.destSlickWrap} ${fewExperienceSlides ? styles.destSlickAlignLeft : ""
                        }`.trim()}
                      key={`experience-slider-${experienceTypes.length}`}
                    >
                      {!fewExperienceSlides ? (
                        <>
                      <button
                        type="button"
                        className={`headerArrowBtn ${styles.destSlickPrev}`}
                        aria-label="Previous experiences"
                        disabled={!canExperiencePrev}
                        onClick={() => experienceSliderRef.current?.slickPrev?.()}
                      >
                        <MdChevronLeft size={22} />
                      </button>
                      <button
                        type="button"
                        className={`headerArrowBtn ${styles.destSlickNext}`}
                        aria-label="Next experiences"
                        disabled={!canExperienceNext}
                        onClick={() => experienceSliderRef.current?.slickNext?.()}
                      >
                        <MdChevronRight size={22} />
                      </button>
                        </>
                      ) : null}
                      <Slider ref={experienceSliderRef} {...experienceSlickSettings}>
                        {experienceTypes.map((item) => (
                          <div key={item.id} className={styles.destSlickSlide}>
                            <button
                              type="button"
                              className={`${styles.typeCard} ${selection.experienceType?.id === item.id ? styles.typeCardActive : ""
                                }`.trim()}
                              onClick={() => handleExperienceSelect(item)}
                            >
                              {item.image ? (
                                <img src={item.image} alt={item.label} className={styles.typeImage} />
                              ) : (
                                <div className={styles.typeImage} />
                              )}
                              <p className={styles.typeLabel}>{item.label}</p>
                            </button>
                          </div>
                        ))}
                      </Slider>
                    </div>
                  </div>
                ) : (
                  <div className={`${styles.typeGrid} ${styles.experienceGrid}`}>
                    {experienceTypes.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`${styles.typeCard} ${selection.experienceType?.id === item.id ? styles.typeCardActive : ""
                          }`.trim()}
                        onClick={() => handleExperienceSelect(item)}
                      >
                        {item.image ? (
                          <img src={item.image} alt={item.label} className={styles.typeImage} />
                        ) : (
                          <div className={styles.typeImage} />
                        )}
                        <p className={styles.typeLabel}>{item.label}</p>
                      </button>
                    ))}
                  </div>
                )}
                {isMobileWizard && loading ? <LoadingBlock /> : null}
              </>
            ) : null}

            {step === "results" ? (
              <>
                <h1 className={styles.title}>
                  Trips Curated <span className={styles.titleAccent}>For You</span>
                </h1>
                <div className={styles.resultsGrid}>
                  {loading ? <LoadingBlock /> : null}
                  {!loading &&
                    results.map((item) => (
                      <SmartSearchPackageCard
                        key={item.packageId || item.id || item.slug}
                        item={item}
                        onViewDeal={setDrawerPackage}
                      />
                    ))}
                </div>
              </>
            ) : null}

            {step === "enquiry" ? (
              <>
                <h1 className={styles.title}>
                  Didn&apos;t Find What You Were{" "}
                  <span className={styles.titleAccent}>Looking For?</span>
                </h1>
                <p className={styles.enquirySubtext}>
                  Let us customize your trip. Our travel experts can create a custom itinerary
                  based on your unique preferences.
                </p>
                <div className={styles.formGrid}>
                  <div className={styles.field}>
                    <label htmlFor="smart-search-name">Full Name</label>
                    <input
                      id="smart-search-name"
                      value={form.fullName}
                      onChange={(event) => {
                        setForm((prev) => ({ ...prev, fullName: event.target.value }));
                        setFormErrors((prev) => ({ ...prev, fullName: "" }));
                      }}
                    />
                    {formErrors.fullName ? (
                      <span className={styles.errorText}>{formErrors.fullName}</span>
                    ) : null}
                  </div>
                  <div className={styles.formRow2}>
                    <div className={styles.field}>
                      <label htmlFor="smart-search-email">Email Address</label>
                      <input
                        id="smart-search-email"
                        type="email"
                        value={form.email}
                        onChange={(event) => {
                          setForm((prev) => ({ ...prev, email: event.target.value }));
                          setFormErrors((prev) => ({ ...prev, email: "" }));
                        }}
                      />
                      {formErrors.email ? (
                        <span className={styles.errorText}>{formErrors.email}</span>
                      ) : null}
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="smart-search-phone">Phone Number</label>
                      <PhoneInputField
                        id="smart-search-phone"
                        value={form.phone}
                        onChange={(value) => {
                          setForm((prev) => ({ ...prev, phone: value || "" }));
                          setFormErrors((prev) => ({ ...prev, phone: "" }));
                        }}
                        error={Boolean(formErrors.phone)}
                      />
                      {formErrors.phone ? (
                        <span className={styles.errorText}>{formErrors.phone}</span>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.formRow2}>
                    <div className={styles.field}>
                      <label htmlFor="smart-search-destination">Destination</label>
                      <input
                        id="smart-search-destination"
                        value={form.destination}
                        onChange={(event) => {
                          setForm((prev) => ({ ...prev, destination: event.target.value }));
                          setFormErrors((prev) => ({ ...prev, destination: "" }));
                        }}
                        readOnly
                      />
                      {formErrors.destination ? (
                        <span className={styles.errorText}>{formErrors.destination}</span>
                      ) : null}
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="smart-search-month">Travel Month</label>
                      <select
                        id="smart-search-month"
                        value={form.travelMonth}
                        onChange={(event) => {
                          setForm((prev) => ({ ...prev, travelMonth: event.target.value }));
                          setFormErrors((prev) => ({ ...prev, travelMonth: "" }));
                        }}
                      >
                        <option value="">Select month</option>
                        {TRAVEL_MONTHS.map((month) => (
                          <option key={month} value={month}>
                            {month}
                          </option>
                        ))}
                      </select>
                      {formErrors.travelMonth ? (
                        <span className={styles.errorText}>{formErrors.travelMonth}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            {showWizardFooter ? (
              <div className={styles.footer}>
                {showFooterBack && step !== 1 && step !== "results" && step !== "enquiry" ? (
                  <button type="button" className={styles.backBtn} onClick={handleBack}>
                    <PiArrowBendUpLeftBold size={16} />
                    Back
                  </button>
                ) : showFooterBack && step === "results" ? (
                  <button type="button" className={styles.backBtn} onClick={handleBack}>
                    <PiArrowBendUpLeftBold size={16} />
                    Back
                  </button>
                ) : showFooterBack && step === "enquiry" ? (
                  <button type="button" className={styles.backBtn} onClick={handleBack}>
                    <PiArrowBendUpLeftBold size={16} />
                    Back
                  </button>
                ) : (
                  <span className={styles.footerSpacer} />
                )}

                {step === "enquiry" ? (
                  <button
                    type="button"
                    className={styles.submitBtn}
                    disabled={loading}
                    onClick={handleSubmitEnquiry}
                  >
                    {loading ? (
                      <span className={styles.btnSpinner} role="status" aria-label="Loading" />
                    ) : (
                      "Get My Custom Itinerary"
                    )}
                  </button>
                ) : step === "results" ? (
                  <button
                    type="button"
                    className={styles.customBtn}
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        destination: selection.destination?.label || prev.destination,
                      }));
                      setStep("enquiry");
                    }}
                  >
                    Customize My Itinerary
                  </button>
                ) : !isMobileWizard ? (
                  <button
                    type="button"
                    className={styles.continueBtn}
                    disabled={!canContinue || loading}
                    onClick={handleContinue}
                  >
                    {loading ? (
                      <span className={styles.btnSpinner} role="status" aria-label="Loading" />
                    ) : (
                      "Continue"
                    )}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <SmartSearchDrawer
        open={Boolean(drawerPackage)}
        packageItem={drawerPackage}
        summary={summary}
        onClose={() => setDrawerPackage(null)}
      />
    </>,
    document.body
  );
}
